import { motion } from "framer-motion";
import enTemplesData from "~/data/about-us/temples.json";
import gujTemplesData from "~/data/about-us/temples_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { BACKGROUND_IMAGE_STYLE } from "~/routes/$lang._index";

// Hero Section
const HeroSection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}>{templesData.title}</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6" />
            <p className={cn("text-lg md:text-xl text-textSecondaryColor italic max-w-3xl mx-auto", medium)}>{templesData.description}</p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 px-4 md:px-6 "
    >
      <div className="max-w-6xl mx-auto  hover:rounded-2xl ">
        <motion.div className="relative rounded-xl">
          {/* Decorative temple icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute -top-6 left-6 md:left-12 w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center shadow-lg z-10 "
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </motion.div>

          <div className="p-8 md:p-12 rounded-2xl shadow-xl" style={BACKGROUND_IMAGE_STYLE}>
            <h2 className={cn("text-xl md:text-2xl font-bold text-primaryColor mb-6", medium)}>{templesData.scaresPathOfMoksha.title}</h2>
            {templesData.scaresPathOfMoksha.description.map((point, index) => (
              <p
                key={index}
                className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
              >
                {point}
              </p>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Three Intentions Section
const ThreeIntentionsSection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-6 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {templesData.sacredIntentions.title}
          </h2>
          {templesData.sacredIntentions.description.map((point, index) => (
            <p
              key={index}
              className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
            >
              {point}
            </p>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Sanskrit Verse Section
const SanskritVerseSection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.7 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div className="p-8 md:p-12 rounded-2xl border-4 border-primaryColor/30 shadow-xl" style={BACKGROUND_IMAGE_STYLE}>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={isVisible ? { scale: 1 } : { scale: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="w-16 h-16 bg-primaryColor rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
            >
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
            </motion.div>

            {templesData.sacredIntentions.sanskritVerse.lines.map((line, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ delay: 0.5 + index * 0.2 }}
                className={cn("text-primaryColor text-lg md:text-xl leading-relaxed italic mb-2", normalInter)}
              >
                {line}
              </motion.p>
            ))}

            <motion.div
              initial={{ scaleX: 0 }}
              animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto my-6"
            />

            <motion.p
              initial={{ opacity: 0 }}
              animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 1.1 }}
              className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed", normalInter)}
            >
              {templesData.sacredIntentions.sanskritVerse.translation}
            </motion.p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Image Section
const ImageSection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primaryColor/30 hover:border-primaryColor/60 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          <motion.img
            transition={{ duration: 0.5 }}
            src={templesData.mainImage.src}
            alt={templesData.mainImage.alt}
            className="w-full aspect-auto object-contain"
            loading="lazy"
          />
        </motion.div>
      </div>
    </motion.section>
  );
};

// History Section
const HistorySection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: 40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
      transition={{ duration: 0.7 }}
      className="mb-12 md:mb-20 px-4 md:px-6 "
    >
      <div className="max-w-6xl mx-auto">
        <motion.div whileHover={{ x: 2, boxShadow: "0 15px 40px rgba(217, 126, 87, 0.2)" }} className="relative rounded-l-2xl">
          {/* Decorative side line */}
          <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-b from-primaryColor via-primaryColor/60 to-primaryColor/20 rounded-full" />

          <div className="pr-8 md:pr-12 bg-gradient-to-l from-secondaryColor/30 to-transparent p-8 md:p-10 rounded-l-2xl border border-primaryColor/20 hover:border-primaryColor/50 transition-all duration-300">
            <h2 className={cn("text-2xl md:text-3xl font-bold text-primaryColor mb-6", medium)}>{templesData.divineConstruction.title}</h2>
            {templesData.divineConstruction.description.map((point, index) => (
              <p
                key={index}
                className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
              >
                {point}
              </p>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Temples Table Section
const TemplesTableSection = ({ templesData }: { templesData: typeof enTemplesData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-10"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {templesData.sixTemples.title}
          </h2>
          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg mt-4", normalInter)}>{templesData.sixTemples.p1}</p>
        </motion.div>

        {/* Desktop/Tablet Table View */}
        <div className="hidden md:block overflow-x-auto  rounded-2xl shadow-lg mb-10">
          <motion.div variants={containerVariants} className="bg-white rounded-2xl shadow-xl border-2 border-primaryColor/20 overflow-hidden">
            <div className="bg-gradient-to-r from-primaryColor to-primaryColor/80 p-4">
              <div className="grid grid-cols-3 gap-4">
                {templesData.sixTemples.table.headers.map((header, index) => (
                  <div key={index} className={cn("text-white font-bold text-center", medium)}>
                    {header}
                  </div>
                ))}
              </div>
            </div>

            {templesData.sixTemples.table.rows.map((temple, index) => (
              <motion.div
                key={index}
                variants={rowVariants}
                whileHover={{ backgroundColor: "rgba(217, 126, 87, 0.05)" }}
                className={`grid grid-cols-3 gap-4 p-4 border-b border-primaryColor/10 ${
                  index === templesData.sixTemples.table.rows.length - 1 ? "border-b-0" : ""
                }`}
              >
                <div className={cn("text-textColor font-semibold text-center", medium)}>{temple[0]}</div>
                <div className={cn("text-textSecondaryColor text-center", normalInter)}>{temple[1]}</div>
                <div className={cn("text-textSecondaryColor text-center", normalInter)} dangerouslySetInnerHTML={{ __html: temple[2] }} />
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile Card View */}
        <motion.div variants={containerVariants} className="md:hidden space-y-4 mb-10">
          {templesData.sixTemples.table.rows.map((temple, index) => (
            <motion.div
              key={index}
              variants={rowVariants}
              whileHover={{ scale: 1.02 }}
              className="bg-gradient-to-br from-white via-secondaryColor/20 to-primaryColor/10 p-6 rounded-xl border-2 border-primaryColor/30 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primaryColor rounded-full flex items-center justify-center shadow-md">
                  <span className={cn("text-white text-lg font-bold", medium)}>{index + 1}</span>
                </div>
                <h3 className={cn("text-xl font-bold text-primaryColor", medium)}>{temple[0]}</h3>
              </div>

              <div className="space-y-2">
                <div>
                  <span className={cn("text-textColor font-semibold block mb-1", medium)}>{templesData.sixTemples.table.headers[1]}:</span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{temple[1]}</span>
                </div>
                <div>
                  <span className={cn("text-textColor font-semibold block mb-1", medium)}>{templesData.sixTemples.table.headers[2]}:</span>
                  <span className={cn("text-textSecondaryColor", normalInter)} dangerouslySetInnerHTML={{ __html: temple[2] }} />
                </div>
              </div>

              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mt-4 h-1 bg-gradient-to-r from-primaryColor to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

function Temples() {
  const lang = useLanguageParams();
  const templesData = lang === "gu" ? gujTemplesData : enTemplesData;

  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      {/* py-8 md:py-16 */}
      <div className="w-full ">
        <HeroSection templesData={templesData} />
        <IntroductionSection templesData={templesData} />
        <ThreeIntentionsSection templesData={templesData} />
        <SanskritVerseSection templesData={templesData} />
        <ImageSection templesData={templesData} />
        <HistorySection templesData={templesData} />
        <TemplesTableSection templesData={templesData} />
      </div>
    </div>
  );
}

export default Temples;
